import { Tooltip } from '@mantine/core';
import {
  IconApps,
  IconCreditCard,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageProvider';
import { useAppSwitcherContext } from '../AppSwitcherProvider';
import { useOrgContext } from '../org/OrgProvider';
import { isAdmin } from '../../utils/admin';
import { useSidebarContext } from './SidebarProvider';
import sidebarClasses from './Sidebar.module.css';

export const SidebarUtilities = () => {
  const { messages } = useLanguage();
  const { app } = useAppSwitcherContext();
  const { org, userOrg } = useOrgContext();
  const { expanded, closeMobile } = useSidebarContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!org) return null;

  const items = [
    {
      label: messages.header.myProfile || 'Profile',
      icon: <IconUser size={18} />,
      path: `/${app}/orgs/${org.id}/profile`,
      match: '/profile',
      show: true,
    },
    {
      label: messages.header.manageUsers || 'Users',
      icon: <IconUsers size={18} />,
      path: `/${app}/orgs/${org.id}/users/view`,
      match: '/users',
      show: isAdmin(userOrg?.role),
    },
    {
      label: messages.header.managePlan || 'Manage Plan',
      icon: <IconCreditCard size={18} />,
      path: `/${app}/orgs/${org.id}/checkout`,
      match: '/checkout',
      show: isAdmin(userOrg?.role),
    },
    {
      label: messages.header.tutorials || 'Tutorials',
      icon: <IconApps size={18} />,
      path: '/demos',
      external: true,
      show: true,
    },
  ].filter(item => item.show);

  const handleClick = (item: (typeof items)[0]) => {
    if (item.external) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.path);
    }
    closeMobile();
  };

  return (
    <div
      style={{
        padding: '4px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {items.map(item => {
        const isActive =
          !item.external && item.match && pathname.includes(item.match);
        const btn = (
          <button
            key={item.label}
            type="button"
            onClick={() => handleClick(item)}
            className={!isActive ? sidebarClasses.hoverItem : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: expanded ? '9px 12px' : '9px 0',
              justifyContent: expanded ? 'flex-start' : 'center',
              border: 'none',
              borderRadius: 8,
              background: 'transparent',
              color: isActive ? '#6b46e3' : '#64748b',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: isActive ? 600 : 500,
            }}
          >
            <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>
            {expanded && <span>{item.label}</span>}
          </button>
        );

        if (!expanded) {
          return (
            <Tooltip
              key={item.label}
              label={item.label}
              position="right"
              withArrow
            >
              {btn}
            </Tooltip>
          );
        }

        return <div key={item.label}>{btn}</div>;
      })}
    </div>
  );
};
